import instance from '../utils/axios';

// Remove duplicate interceptors since they're already in the axios instance
export default instance;

export interface Worker {
  id: number;
  worker_id: string;
  name: string;
  status: string;
  joining_date: string;
  adhaar_no: string;
  pan: string;
  bank_account_name: string;
  bank_account_no: string;
  ifsc: string;
  worker_type: 'STITCHING' | 'PRESS';
  phone: string;
  address: string;
  rate?: number | null;
  adhaar_doc?: string | null;
  pan_doc?: string | null;
  bank_doc?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkerDTO {
  worker_id: string;
  name: string;
  status: string;
  joining_date: string;
  adhaar_no: string;
  pan: string;
  bank_account_name: string;
  bank_account_no: string;
  ifsc: string;
  worker_type: 'STITCHING' | 'PRESS';
  phone: string;
  address: string;
  rate?: string;
}

export interface UpdateWorkerDTO {
  worker_id?: string;
  name?: string;
  status?: string;
  joining_date?: string;
  adhaar_no?: string;
  pan?: string;
  bank_account_name?: string;
  bank_account_no?: string;
  ifsc?: string;
  worker_type?: 'STITCHING' | 'PRESS';
  phone?: string;
  address?: string;
  rate?: string;
}

export const workerApi = {
  getAll: async (params?: { worker_type?: string }): Promise<Worker[]> => {
    const response = await instance.get<Worker[]>('/workers/', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Worker> => {
    const response = await instance.get<Worker>(`/workers/${id}`);
    return response.data;
  },

  create: async (data: CreateWorkerDTO): Promise<Worker> => {
    const response = await instance.post<Worker>('/workers/', data);
    return response.data;
  },

  update: async (id: number, data: UpdateWorkerDTO): Promise<Worker> => {
    const response = await instance.put<Worker>(`/workers/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    try {
      const response = await instance.delete(`/workers/${id}`);
      // Handle 204 No Content response
      console.log(`Worker delete response status: ${response.status}`);
      return;
    } catch (error) {
      console.error(`Worker delete error for ID ${id}:`, error);
      throw error; // Re-throw to let the calling code handle it
    }
  },

  deleteReceive: async (assignmentId: number, receiveId: number) => {
    try {
      const response = await instance.delete(`/worker-assignments/${assignmentId}/receives/${receiveId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting receive record:', error);
      throw error;
    }
  },
};

export interface DesignCutting {
  id: number;
  design_no: string;
  received_date: string;
  inner_received_date?: string;
  image_url?: string;
  garment_part: 'TOP' | 'BOTTOM' | 'JACKET';
  batch_number: number;
  batch_date: string;
  batch_notes?: string;
  fabric_meter: number;
  inner_fabric_meter?: number;
  xs_pieces: number;
  s_pieces: number;
  m_pieces: number;
  l_pieces: number;
  xl_pieces: number;
  xxl_pieces: number;
  three_xl_pieces: number;
  total_pieces: number;
  cutting_date?: string;
  rate?: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  notes?: string;
  avg_fabric_per_piece?: number;
  avg_inner_fabric_per_piece?: number;
  createdAt: string;
  updatedAt: string;
  challan_date?: string;
}

export interface CreateDesignCuttingDTO {
  design_no: string;
  received_date: string;
  inner_received_date?: string;
  garment_part: 'TOP' | 'BOTTOM' | 'JACKET';
  fabric_meter: number;
  inner_fabric_meter?: number;
  xs_pieces: number;
  s_pieces: number;
  m_pieces: number;
  l_pieces: number;
  xl_pieces: number;
  xxl_pieces: number;
  three_xl_pieces: number;
  cutting_date?: string;
  rate?: number;
  notes?: string;
  batch_number?: number;
  production_order_id?: number;
}

export interface UpdateDesignCuttingDTO {
  design_no?: string;
  received_date?: string;
  inner_received_date?: string;
  garment_part?: 'TOP' | 'BOTTOM' | 'JACKET';
  fabric_meter?: number;
  inner_fabric_meter?: number;
  xs_pieces?: number;
  s_pieces?: number;
  m_pieces?: number;
  l_pieces?: number;
  xl_pieces?: number;
  xxl_pieces?: number;
  three_xl_pieces?: number;
  cutting_date?: string;
  rate?: number;
  notes?: string;
}

export const designCuttingApi = {
  getAll: async (): Promise<DesignCutting[]> => {
    try {
      console.log('Making request to /design-cutting');
      const response = await instance.get<DesignCutting[]>('/design-cutting');
      console.log('Response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in designCuttingApi.getAll:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },

  getById: async (id: number): Promise<DesignCutting> => {
    try {
      console.log(`Making request to /design-cutting/${id}`);
      const response = await instance.get<DesignCutting>(`/design-cutting/${id}`);
      console.log('Response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in designCuttingApi.getById:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },

  create: async (data: CreateDesignCuttingDTO): Promise<DesignCutting> => {
    try {
      console.log('Making request to /design-cutting (POST)');
      const response = await instance.post<DesignCutting>('/design-cutting', data);
      console.log('Response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in designCuttingApi.create:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },

  update: async (id: number, data: UpdateDesignCuttingDTO): Promise<DesignCutting> => {
    try {
      console.log(`Making request to /design-cutting/${id} (PUT)`);
      const response = await instance.put<DesignCutting>(`/design-cutting/${id}`, data);
      console.log('Response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in designCuttingApi.update:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },

  delete: async (id: number): Promise<any> => {
    try {
      console.log(`Making request to /design-cutting/${id} (DELETE)`);
      const response = await instance.delete(`/design-cutting/${id}`);
      console.log('Response received:', response.status, response.statusText);
      return response.data; // Return the response data which includes jobWorksUpdated
    } catch (error: any) {
      console.error('Error in designCuttingApi.delete:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    }
  },
};

// Shortfall API
export const shortfallApi = {
  create: async (payload: { design_no: string; garment_part: string; size: string; quantity: number; status?: 'OPEN'|'CLOSED'; reason?: string; source?: string }) => {
    const res = await instance.post('/worker-assignments/shortfalls', payload);
    return res.data;
  },
  update: async (id: number, payload: { quantity?: number; status?: 'OPEN'|'CLOSED'; reason?: string }) => {
    const res = await instance.put(`/worker-assignments/shortfalls/${id}`, payload);
    return res.data;
  },
  summary: async (design_no: string, garment_part?: string): Promise<Record<string, { open: number; closed: number }>> => {
    const res = await instance.get('/worker-assignments/shortfalls/summary', { params: { design_no, garment_part } });
    return res.data || {};
  }
};

// Add User interfaces
export interface UserPermission {
  module: string;
  can_view: boolean;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  role_id?: number | null;
  is_active: boolean;
  permissions: UserPermission[];
  assigned_role?: { id: number; name: string } | null;
}

export interface CreateUserDTO {
  name: string;
  email: string;
  password: string;
  role: string;
  role_id?: number | null;
  permissions: UserPermission[];
}

export interface UpdateUserDTO {
  name?: string;
  email?: string;
  role?: string;
  role_id?: number | null;
  is_active?: boolean;
  permissions?: UserPermission[];
}

// Role interfaces
export interface Role {
  id: number;
  name: string;
  description?: string | null;
  permissions: UserPermission[];
  _count?: { users: number };
}

export interface CreateRoleDTO {
  name: string;
  description?: string;
  permissions: UserPermission[];
}

export interface UpdateRoleDTO {
  name: string;
  description?: string;
  permissions: UserPermission[];
}

// Add Users API
export const usersApi = {
  getAll: async (): Promise<User[]> => {
    try {
      console.log('Making request to /users');
      const response = await instance.get<User[]>('/users');
      console.log('Users response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in usersApi.getAll:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  getCompanyUsers: async (companyId: number): Promise<User[]> => {
    try {
      console.log(`Making request to /companies/${companyId}/users`);
      const response = await instance.get<User[]>(`/companies/${companyId}/users`);
      console.log('Company users response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in usersApi.getCompanyUsers:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  create: async (companyId: number, data: CreateUserDTO): Promise<User> => {
    try {
      console.log(`Making request to /companies/${companyId}/users (POST)`);
      const response = await instance.post<User>(`/companies/${companyId}/users`, data);
      console.log('Create user response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in usersApi.create:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  update: async (companyId: number, userId: number, data: UpdateUserDTO): Promise<User> => {
    try {
      console.log(`Making request to /companies/${companyId}/users/${userId} (PUT)`);
      const response = await instance.put<User>(`/companies/${companyId}/users/${userId}`, data);
      console.log('Update user response received:', response.status, response.statusText);
      return response.data;
    } catch (error: any) {
      console.error('Error in usersApi.update:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  delete: async (companyId: number, userId: number): Promise<void> => {
    try {
      console.log(`Making request to /companies/${companyId}/users/${userId} (DELETE)`);
      const response = await instance.delete(`/companies/${companyId}/users/${userId}`);
      console.log('Delete user response received:', response.status, response.statusText);
    } catch (error: any) {
      console.error('Error in usersApi.delete:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  deactivate: async (companyId: number, userId: number): Promise<void> => {
    try {
      console.log(`Making request to /companies/${companyId}/users/${userId} (DELETE)`);
      const response = await instance.delete(`/companies/${companyId}/users/${userId}`);
      console.log('Deactivate user response received:', response.status, response.statusText);
    } catch (error: any) {
      console.error('Error in usersApi.deactivate:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  reactivate: async (companyId: number, userId: number): Promise<void> => {
    try {
      console.log(`Making request to /companies/${companyId}/users/${userId}/reactivate (PUT)`);
      const response = await instance.put(`/companies/${companyId}/users/${userId}/reactivate`);
      console.log('Reactivate user response received:', response.status, response.statusText);
    } catch (error: any) {
      console.error('Error in usersApi.reactivate:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  // Change user password (Master only)
  changePassword: async (companyId: number, userId: number, data: { password: string }): Promise<void> => {
    try {
      console.log(`Making request to /companies/${companyId}/users/${userId}/password (PATCH)`);
      const response = await instance.patch(`/companies/${companyId}/users/${userId}/password`, data);
      console.log('Change password response received:', response.status, response.statusText);
    } catch (error: any) {
      console.error('Error in usersApi.changePassword:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  }
};

// Roles API
export const rolesApi = {
  getAll: async (companyId: number): Promise<Role[]> => {
    const response = await instance.get<Role[]>(`/companies/${companyId}/roles`);
    return response.data;
  },

  create: async (companyId: number, data: CreateRoleDTO): Promise<Role> => {
    const response = await instance.post<Role>(`/companies/${companyId}/roles`, data);
    return response.data;
  },

  update: async (companyId: number, roleId: number, data: UpdateRoleDTO): Promise<Role> => {
    const response = await instance.put<Role>(`/companies/${companyId}/roles/${roleId}`, data);
    return response.data;
  },

  delete: async (companyId: number, roleId: number): Promise<void> => {
    await instance.delete(`/companies/${companyId}/roles/${roleId}`);
  },
};

// Settings API
export const getJobWorkTypes = () => instance.get('/settings/job-work-types');
export const createJobWorkType = (data: { name: string; description: string; isActive?: boolean }) =>
  instance.post('/settings/job-work-types', data);
export const updateJobWorkType = (id: number, data: { name: string; description: string; isActive?: boolean }) =>
  instance.put(`/settings/job-work-types/${id}`, data);
export const deleteJobWorkType = (id: number) =>
  instance.delete(`/settings/job-work-types/${id}`);

// Job Work Garment Parts API (Settings)
export const getJobWorkGarmentParts = () => instance.get('/settings/job-work-garment-parts');
export const createJobWorkGarmentPart = (data: { name: string; description?: string; isActive?: boolean }) =>
  instance.post('/settings/job-work-garment-parts', data);
export const updateJobWorkGarmentPart = (id: number, data: { name: string; description?: string; isActive?: boolean }) =>
  instance.put(`/settings/job-work-garment-parts/${id}`, data);
export const deleteJobWorkGarmentPart = (id: number) =>
  instance.delete(`/settings/job-work-garment-parts/${id}`);

// Dupatta Types API (Settings)
export const getDupattaTypes = () => instance.get('/settings/dupatta-types');
export const createDupattaType = (data: { name: string; description?: string; isActive?: boolean }) =>
  instance.post('/settings/dupatta-types', data);
export const updateDupattaType = (id: number, data: { name: string; description?: string; isActive?: boolean }) =>
  instance.put(`/settings/dupatta-types/${id}`, data);
export const deleteDupattaType = (id: number) =>
  instance.delete(`/settings/dupatta-types/${id}`);

// Material Qualities API (Settings)
export const getMaterialQualities = () => instance.get('/settings/material-qualities');
export const createMaterialQuality = (data: { name: string; isActive?: boolean }) =>
  instance.post('/settings/material-qualities', data);
export const updateMaterialQuality = (id: number, data: { name: string; isActive?: boolean }) =>
  instance.put(`/settings/material-qualities/${id}`, data);
export const deleteMaterialQuality = (id: number) =>
  instance.delete(`/settings/material-qualities/${id}`);

// Job Work API functions
export const createJobWork = (data: any) => {
  return instance.post('/job-works', data);
};

// Vendor API functions
export const getVendors = () => {
  return instance.get('/vendors');
};

// Design API functions
export const getDesigns = () => {
  return instance.get('/designs');
}; 